import { useCallback, useState } from "react";

import type { Aggregation, Metric, Mode, Preset, ViewRange } from "./types";

const YEAR_MS = 365 * 24 * 60 * 60 * 1000;

/**
 * The visible window for a preset. "1Y"/"3Y" show that many years up to the end
 * of the data (forecast end when present); "All"/null show the full span.
 * `nowMs` is the history/forecast boundary (item detail) or span.max (explore).
 */
export function presetWindow(
  preset: Preset | null,
  span: ViewRange,
  nowMs: number,
): ViewRange {
  if (preset === "1Y" || preset === "3Y") {
    const years = preset === "1Y" ? 1 : 3;
    return { min: Math.max(span.min, nowMs - years * YEAR_MS), max: span.max };
  }
  return span;
}

export type UseDemandChartViewOptions = {
  initialPreset?: Preset;
  initialAggregation?: Aggregation;
  initialMetric?: Metric;
};

/** Owns the chart's view/interaction state, shared by both demand views. */
export function useDemandChartView(opts: UseDemandChartViewOptions = {}) {
  const [aggregation, setAggregation] = useState<Aggregation>(
    opts.initialAggregation ?? "month",
  );
  const [metric, setMetric] = useState<Metric>(
    opts.initialMetric ?? "quantity",
  );
  const [mode, setMode] = useState<Mode>("raw");
  const [defaultPreset] = useState<Preset>(opts.initialPreset ?? "1Y");
  const [activePreset, setActivePreset] = useState<Preset | null>(
    defaultPreset,
  );
  const [viewRange, setViewRange] = useState<ViewRange | null>(null);

  /** Effective visible window: a manual range wins, else the preset window. */
  const resolveView = useCallback(
    (span: ViewRange, nowMs: number): ViewRange =>
      viewRange ?? presetWindow(activePreset, span, nowMs),
    [viewRange, activePreset],
  );

  const handlePreset = useCallback((p: Preset) => {
    setActivePreset(p);
    setViewRange(null);
  }, []);

  /**
   * Record a user pan/zoom from the chart. The echo-guard (ignoring
   * chartjs-plugin-zoom replaying our own programmatic min/max from preset
   * clicks / data loads) lives in TimeseriesCanvas's `pushIfChanged`, so this
   * only fires on a genuine gesture — we just store the range and drop the preset.
   */
  const handleUserRange = useCallback((range: ViewRange) => {
    setViewRange(range);
    setActivePreset(null);
  }, []);

  /** Clear a manual zoom, snapping back to the initial preset. */
  const reset = useCallback(() => {
    setViewRange(null);
    setActivePreset(defaultPreset);
  }, [defaultPreset]);

  return {
    aggregation,
    setAggregation,
    metric,
    setMetric,
    mode,
    setMode,
    activePreset,
    setActivePreset,
    viewRange,
    setViewRange,
    resolveView,
    handlePreset,
    handleUserRange,
    reset,
  };
}
